<template>
  <div
    ref="overviewEl"
    class="relative h-24 w-full touch-none select-none overflow-hidden rounded border border-slate-600 bg-slate-950"
    title="Navigate the full song"
    @pointerdown="startInteraction"
    @pointermove="moveInteraction"
    @pointerup="endInteraction"
    @pointercancel="endInteraction"
  >
    <canvas ref="canvasEl" class="absolute inset-0 h-full w-full pointer-events-none"></canvas>
    <div class="absolute inset-y-0 border border-cyan-200/90 bg-cyan-300/15 pointer-events-none" :style="viewportStyle"></div>
    <div class="absolute inset-y-0 w-px bg-white pointer-events-none" :style="playheadStyle"></div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { buildAbsoluteSequenceTiming } from './synthSequenceGeometry.js'
import { getSynthTrackColor } from './synthTrackColors.js'
import { buildOverviewLaneGeometry, durationToOverviewWidth, overviewViewportGeometry, overviewXToScrollLeft, overviewXToTime, timeToOverviewX } from './synthSequenceOverviewGeometry.js'

const props = defineProps({
  tracks: { type: Array, required: true },
  bpm: { type: Number, default: 120 },
  playbackTimeMs: { type: Number, default: 0 },
  viewport: { type: Object, default: () => ({ scrollLeft: 0, viewportWidth: 0, contentWidth: 0 }) },
})

const emit = defineEmits(['seek', 'focus', 'pan'])
const overviewEl = ref(null)
const canvasEl = ref(null)
const width = ref(0)
const height = ref(0)
let resizeObserver = null
let draggingViewport = false
let viewportDragOffset = 0

const timedTracks = computed(() => props.tracks.map((track, trackIndex) => ({
  trackIndex,
  events: buildAbsoluteSequenceTiming(track?.sequence, props.bpm),
})))
const totalDurationMs = computed(() => Math.max(0, ...timedTracks.value.flatMap(track => track.events.map(event => event.endMs))))
const viewport = computed(() => overviewViewportGeometry({ ...props.viewport, overviewWidth: width.value }))
const viewportStyle = computed(() => ({ left: `${viewport.value.left}px`, width: `${viewport.value.width}px` }))
const playheadStyle = computed(() => ({ left: `${timeToOverviewX(props.playbackTimeMs, totalDurationMs.value, width.value)}px` }))

watch([timedTracks, width, height], drawOverview)

function drawOverview() {
  const canvas = canvasEl.value
  if (!canvas || width.value <= 0 || height.value <= 0) return
  const ratio = window.devicePixelRatio || 1
  canvas.width = Math.max(1, Math.round(width.value * ratio))
  canvas.height = Math.max(1, Math.round(height.value * ratio))
  const context = canvas.getContext('2d')
  context.setTransform(ratio, 0, 0, ratio, 0, 0)
  context.clearRect(0, 0, width.value, height.value)
  const lanes = buildOverviewLaneGeometry(props.tracks.length, height.value)
  timedTracks.value.forEach(track => {
    const lane = lanes[track.trackIndex]
    if (!lane) return
    context.fillStyle = getSynthTrackColor(track.trackIndex).solid
    for (const event of track.events) {
      const x = timeToOverviewX(event.startMs, totalDurationMs.value, width.value)
      const eventWidth = durationToOverviewWidth(event.durationMs, totalDurationMs.value, width.value)
      context.fillRect(x, lane.y + 1, eventWidth, Math.max(1, lane.height - 2))
    }
  })
}

function localX(event) {
  const rect = overviewEl.value?.getBoundingClientRect()
  return rect ? Math.max(0, Math.min(rect.width, event.clientX - rect.left)) : 0
}

function startInteraction(event) {
  const x = localX(event)
  draggingViewport = x >= viewport.value.left && x <= viewport.value.left + viewport.value.width
  viewportDragOffset = draggingViewport ? x - viewport.value.left : viewport.value.width / 2
  overviewEl.value?.setPointerCapture(event.pointerId)
  if (draggingViewport) panTo(x - viewportDragOffset + viewport.value.width / 2)
  else {
    const targetTime = overviewXToTime(x, width.value, totalDurationMs.value)
    emit('seek', targetTime)
    emit('focus', targetTime)
  }
}

function moveInteraction(event) {
  if (draggingViewport) panTo(localX(event) - viewportDragOffset + viewport.value.width / 2)
}

function endInteraction(event) {
  draggingViewport = false
  overviewEl.value?.releasePointerCapture(event.pointerId)
}

function panTo(x) {
  emit('pan', overviewXToScrollLeft(x, width.value, props.viewport.contentWidth, props.viewport.viewportWidth))
}

onMounted(() => {
  resizeObserver = new ResizeObserver(entries => {
    width.value = Math.max(1, entries[0]?.contentRect.width ?? 1)
    height.value = Math.max(1, entries[0]?.contentRect.height ?? 1)
    nextTick(drawOverview)
  })
  if (overviewEl.value) resizeObserver.observe(overviewEl.value)
})

onBeforeUnmount(() => resizeObserver?.disconnect())
</script>
